import React, { useState, useEffect, useRef } from 'react';
import { 
    Card, Row, Col, Statistic, Table, Tag, Button, Space, Modal, Badge, 
    Progress, Tooltip, message, Spin, Form, Select, Alert, Input 
} from 'antd';
import { 
    CalendarOutlined, TeamOutlined, CheckCircleOutlined, ClockCircleOutlined,
    PlusOutlined, ReloadOutlined, ExportOutlined, UserAddOutlined, 
    EditOutlined, EyeOutlined, DeleteOutlined, WarningOutlined
} from '@ant-design/icons';
import WorkPlanService from '../services/Workplanservice';
import WorkPlanForm from './workplanform';
import VisitScheduleForm, { AssetTypeTags } from './Visitscheduleform';
import StatusUpdateModal from './Statusupdatemodal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import moment from 'moment';
import './Workplanintegration.css';

const { Option } = Select;
const { TextArea } = Input;

const WorkPlanIntegration = ({
    selectedRegion,
    selectedLevel,
    isVisible = true
}) => {
    // State management
    const [workPlans, setWorkPlans] = useState([]);
    const [visits, setVisits] = useState([]);
    const [statistics, setStatistics] = useState({
        total_visits: 0,
        pending_visits: 0,
        in_progress_visits: 0,
        completed_visits: 0,
        cancelled_visits: 0,
        overdue_visits: 0,
        total_assets: 0,
        completion_rate: 0
    });
    const [loading, setLoading] = useState(false);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [selectedVisit, setSelectedVisit] = useState(null);

    // Form modal states
    const [workPlanFormVisible, setWorkPlanFormVisible] = useState(false);
    const [visitScheduleFormVisible, setVisitScheduleFormVisible] = useState(false);

    // Form data states
    const [editingWorkPlan, setEditingWorkPlan] = useState(null);
    const [selectedWorkPlanForVisit, setSelectedWorkPlanForVisit] = useState(null);

    // Status update modal
    const [statusUpdateModal, setStatusUpdateModal] = useState(false);
    const [selectedVisitForStatus, setSelectedVisitForStatus] = useState(null);
    const [statusUpdateForm] = Form.useForm();

    const componentRef = useRef();

    // ── Statistics calculation ────────────────────────────────────────────────
    const calculateStatisticsFromVisits = (visitsData) => {
        if (!visitsData || visitsData.length === 0) {
            return {
                total_visits: 0, pending_visits: 0, in_progress_visits: 0,
                completed_visits: 0, cancelled_visits: 0, overdue_visits: 0,
                total_assets: 0, completion_rate: 0
            };
        }

        const total       = visitsData.length;
        const pending     = visitsData.filter(v => (v.display_status || v.status) === 'pending').length;
        const inProgress  = visitsData.filter(v => (v.display_status || v.status) === 'in_progress').length;
        const completed   = visitsData.filter(v => v.status === 'completed').length;
        const cancelled   = visitsData.filter(v => v.status === 'cancelled').length;
        const overdue     = visitsData.filter(v => (v.display_status || v.status) === 'overdue').length;
        const totalAssets = visitsData.reduce((sum, v) => sum + (parseInt(v.assets_recorded_count) || 0), 0);
        const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

        return { total_visits: total, pending_visits: pending, in_progress_visits: inProgress,
                 completed_visits: completed, cancelled_visits: cancelled, overdue_visits: overdue,
                 total_assets: totalAssets, completion_rate: completionRate };
    };

    useEffect(() => {
        if (visits && visits.length > 0) {
            setStatistics(calculateStatisticsFromVisits(visits));
        } else {
            setStatistics({
                total_visits: 0, pending_visits: 0, in_progress_visits: 0,
                completed_visits: 0, cancelled_visits: 0, overdue_visits: 0,
                total_assets: 0, completion_rate: 0
            });
        }
    }, [visits]);

    useEffect(() => {
        if (isVisible) loadAllData();
    }, [isVisible, selectedRegion, selectedLevel]);

    // ── Data loading ──────────────────────────────────────────────────────────
    const loadAllData = async () => {
        setLoading(true);
        try {
            await Promise.all([loadWorkPlans(), loadVisits()]);
        } catch (error) {
            console.error('Error loading work plan data:', error);
            message.error('Failed to load work plan data');
        } finally {
            setLoading(false);
        }
    };

    const loadWorkPlans = async () => {
        try {
            const filters = { status: 'in_progress,planned' };
            if (selectedRegion && selectedRegion !== 'All Regions') filters.region = selectedRegion;
            const response = await WorkPlanService.getActiveWorkPlans(filters);
            if (response.code === 200) setWorkPlans(response.result.data || []);
        } catch (error) {
            console.error('Error loading work plans:', error);
        }
    };

    const loadVisits = async () => {
        try {
            const filters = {};
            if (selectedRegion && selectedRegion !== 'All Regions') filters.region = selectedRegion;
            const response = await WorkPlanService.getScheduledVisits(filters);
            if (response.code === 200) setVisits(response.result.data || []);
        } catch (error) {
            console.error('Error loading visits:', error);
        }
    };

    // ── Status update handlers ────────────────────────────────────────────────
    const handleOpenStatusUpdate = (visit) => {
        setSelectedVisitForStatus(visit);
        statusUpdateForm.setFieldsValue({
            status: visit.status,
            notes: visit.notes || '',
            findings: visit.findings || ''
        });
        setStatusUpdateModal(true);
    };

    const handleUpdateStatus = async () => {
        try {
            const values = await statusUpdateForm.validateFields();
            if (values.status === 'completed' && selectedVisitForStatus.assets_recorded_count === 0) {
                Modal.confirm({
                    title: 'No Assets Recorded',
                    content: 'No assets have been recorded for this visit. Are you sure you want to mark it as completed?',
                    okText: 'Yes, Complete Anyway',
                    cancelText: 'Cancel',
                    onOk: async () => { await performStatusUpdate(values); }
                });
                return;
            }
            await performStatusUpdate(values);
        } catch (error) {
            console.log('Validation failed:', error);
        }
    };

    const performStatusUpdate = async (values) => {
        try {
            const response = await WorkPlanService.updateVisitStatus(selectedVisitForStatus.id, values);
            if (response.data.code === 200) {
                message.success(response.data.message || 'Status updated successfully');
                setStatusUpdateModal(false);
                statusUpdateForm.resetFields();
                setSelectedVisitForStatus(null);
                loadAllData();
            } else {
                message.error(response.data.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            message.error(error.response?.data?.message || 'Failed to update status');
        }
    };

    // ── Other handlers ────────────────────────────────────────────────────────
    const handleCreateWorkPlan  = () => { setEditingWorkPlan(null); setWorkPlanFormVisible(true); };
    const handleEditWorkPlan    = (wp) => { setEditingWorkPlan(wp); setWorkPlanFormVisible(true); };
    const handleScheduleVisit   = (wp) => { setSelectedWorkPlanForVisit(wp); setVisitScheduleFormVisible(true); };
    const handleViewDetails     = (visit) => { setSelectedVisit(visit); setDetailsVisible(true); };

    const handleDeleteWorkPlan = (id) => {
        Modal.confirm({
            title: 'Delete Work Plan',
            content: 'Are you sure? All associated visits will also be deleted.',
            okText: 'Delete', okType: 'danger',
            onOk: async () => {
                try { await WorkPlanService.deleteWorkPlan(id); message.success('Work plan deleted'); loadAllData(); }
                catch { message.error('Failed to delete work plan'); }
            }
        });
    };

    const handleDeleteVisit = (id) => {
        Modal.confirm({
            title: 'Delete Visit',
            content: 'Are you sure you want to delete this scheduled visit?',
            okText: 'Delete', okType: 'danger',
            onOk: async () => {
                try { await WorkPlanService.deleteVisit(id); message.success('Visit deleted'); loadAllData(); }
                catch { message.error('Failed to delete visit'); }
            }
        });
    };

    const exportToPDF = async () => {
        const canvas = await html2canvas(componentRef.current, { scale: 2 });
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210;
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, (canvas.height * imgWidth) / canvas.width);
        pdf.save(`work-plan-${moment().format('YYYY-MM-DD')}.pdf`);
        message.success('PDF exported successfully');
    };

    const exportToImage = async () => {
        const canvas = await html2canvas(componentRef.current, { scale: 2 });
        const link = document.createElement('a');
        link.download = `work-plan-${moment().format('YYYY-MM-DD')}.jpg`;
        link.href = canvas.toDataURL('image/jpeg');
        link.click();
        message.success('Image exported successfully');
    };

    // ── Helpers ───────────────────────────────────────────────────────────────
    const getStatusConfig = (status) => {
        const configs = {
            completed:   { color: 'success',    icon: '🟢', text: 'Completed' },
            in_progress: { color: 'processing', icon: '🔵', text: 'In Progress' },
            pending:     { color: 'warning',    icon: '🟡', text: 'Pending' },
            overdue:     { color: 'error',      icon: '🔴', text: 'OVERDUE' },
            cancelled:   { color: 'default',    icon: '⚪', text: 'Cancelled' }
        };
        return configs[status] || configs.pending;
    };

    // Helper to safely parse asset_types JSON from backend
    const parseAssetTypes = (raw) => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        try { return JSON.parse(raw); } catch { return String(raw).split(',').map(s => s.trim()).filter(Boolean); }
    };

    // ── Table columns ─────────────────────────────────────────────────────────
    const columns = [
        {
            title: '#',
            width: 50,
            render: (_, __, index) => index + 1
        },
        {
            title: 'Facility',
            dataIndex: 'facility_name',
            key: 'facility_name',
            width: 200,
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{text}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>{record.site_code} | {record.fac_code}</div>
                </div>
            ),
            sorter: (a, b) => a.facility_name?.localeCompare(b.facility_name)
        },
        {
            title: 'Region',
            dataIndex: 'region',
            key: 'region',
            width: 100,
            filters: [
                { text: 'Hhohho',      value: 'Hhohho' },
                { text: 'Lubombo',     value: 'Lubombo' },
                { text: 'Manzini',     value: 'Manzini' },
                { text: 'Shiselweni',  value: 'Shiselweni' }
            ],
            onFilter: (value, record) => record.region === value,
            render: (region) => <Tag color="blue">{region}</Tag>
        },
        {
            title: 'Facility Type',
            dataIndex: 'facility_level',
            key: 'facility_level',
            width: 120,
            filters: [
                { text: 'Level 2', value: 'Level 2' },
                { text: 'Level 3', value: 'Level 3' },
                { text: 'Level 4', value: 'Level 4' },
                { text: 'Level 5', value: 'Level 5' }
            ],
            onFilter: (value, record) => record.facility_level === value
        },
        {
            title: 'Scheduled Date',
            dataIndex: 'scheduled_date',
            key: 'scheduled_date',
            width: 120,
            render: (date) => moment(date).format('DD MMM YYYY'),
            sorter: (a, b) => moment(a.scheduled_date).unix() - moment(b.scheduled_date).unix()
        },
        // ── NEW: Asset Types column ───────────────────────────────────────────
        {
            title: 'Asset Types',
            dataIndex: 'asset_types',
            key: 'asset_types',
            width: 240,
            filters: [
                { text: '🏥 Non Medical', value: '2' },
                { text: '🚗 Vehicles',    value: '8' },
                { text: '📦 Supplies',    value: '9' },
                { text: '💊 Medical',     value: '3' },
            ],
            onFilter: (value, record) => {
                const types = parseAssetTypes(record.asset_types);
                return types.map(String).includes(String(value));
            },
            render: (raw) => <AssetTypeTags assetTypes={parseAssetTypes(raw)} />
        },
        // ─────────────────────────────────────────────────────────────────────
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 220,
            filters: [
                { text: '🟡 Pending',     value: 'pending' },
                { text: '🔵 In Progress', value: 'in_progress' },
                { text: '🟢 Completed',   value: 'completed' },
                { text: '🔴 Overdue',     value: 'overdue' },
                { text: '⚪ Cancelled',   value: 'cancelled' }
            ],
            onFilter: (value, record) => (record.display_status || record.status) === value,
            render: (status, record) => {
                const displayStatus = record.display_status || status;
                const config = getStatusConfig(displayStatus);
                const isOverdue = displayStatus === 'overdue';
                const hasAssets = record.assets_recorded_count > 0;

                return (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Tag
                            color={isOverdue ? 'error' : config.color}
                            style={{
                                width: '100%', textAlign: 'center', fontWeight: 500,
                                animation: isOverdue ? 'blink 1.5s linear infinite' : 'none'
                            }}
                        >
                            {config.icon} {config.text}
                        </Tag>
                        {hasAssets && (
                            <Tooltip title={`${record.assets_recorded_count} assets recorded on ${moment(record.scheduled_date).format('DD MMM YYYY')}`}>
                                <Tag color="cyan" icon={<CheckCircleOutlined />} style={{ width: '100%', textAlign: 'center' }}>
                                    {record.assets_recorded_count} assets
                                </Tag>
                            </Tooltip>
                        )}
                        {status !== 'completed' && status !== 'cancelled' && (
                            <Button type="primary" size="small" icon={<EditOutlined />}
                                onClick={() => handleOpenStatusUpdate(record)} ghost block>
                                Update Status
                            </Button>
                        )}
                    </Space>
                );
            }
        },
        {
            title: 'Notes',
            dataIndex: 'notes',
            key: 'notes',
            width: 200,
            ellipsis: { showTitle: false },
            render: (notes) => <Tooltip title={notes}>{notes || '—'}</Tooltip>
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="View Details">
                        <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)} />
                    </Tooltip>
                    <Tooltip title="Delete Visit">
                        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteVisit(record.id)} />
                    </Tooltip>
                </Space>
            )
        }
    ];

    if (!isVisible) return null;

    return (
        <div ref={componentRef} className="work-plan-integration">
            <Spin spinning={loading}>
                {/* Header */}
                <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                    <Col>
                        <h2 style={{ margin: 0 }}>
                            <CalendarOutlined /> Work Plan & Visit Tracking
                        </h2>
                    </Col>
                    <Col>
                        <Space>
                            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateWorkPlan}>
                                Create Work Plan
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={loadAllData}>Refresh</Button>
                            <Button.Group>
                                <Button icon={<ExportOutlined />} onClick={exportToPDF}>PDF</Button>
                                <Button icon={<ExportOutlined />} onClick={exportToImage}>Image</Button>
                            </Button.Group>
                        </Space>
                    </Col>
                </Row>

                {/* Statistics */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Card>
                            <Statistic title="Total Scheduled Facilities" value={statistics.total_visits} prefix={<CalendarOutlined />} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Card>
                            <Statistic title="Scheduled & Pending" value={statistics.pending_visits} valueStyle={{ color: '#faad14' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Card>
                            <Statistic title="In Progress" value={statistics.in_progress_visits} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#1890ff' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Card>
                            <Statistic title="Completed" value={statistics.completed_visits} valueStyle={{ color: '#3f8600' }}
                                prefix={<CheckCircleOutlined />} suffix={`/ ${statistics.total_visits}`} />
                            <Progress percent={statistics.completion_rate} size="small" status="active" />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Card>
                            <Statistic title="Overdue" value={statistics.overdue_visits}
                                valueStyle={{ color: statistics.overdue_visits > 0 ? '#cf1322' : '#000' }}
                                prefix={
                                    <Badge count={statistics.overdue_visits} style={{ backgroundColor: '#f5222d' }}>
                                        <WarningOutlined />
                                    </Badge>
                                }
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Visits Table */}
                <Card title={<span><CalendarOutlined /> Scheduled Visits</span>} style={{ marginBottom: 24 }}>
                    <Table
                        columns={columns}
                        dataSource={visits}
                        rowKey="id"
                        pagination={{
                            defaultPageSize: 100,
                            pageSizeOptions: ['10', '20', '50', '100', '1000'],
                            showSizeChanger: true,
                            showTotal: (total) => `Total ${total} visits`
                        }}
                        scroll={{ x: 1400 }}
                    />
                </Card>

                {/* Active Work Plans */}
                <Card title={<span><CalendarOutlined /> Active Work Plans</span>} style={{ marginBottom: 24 }}>
                    {workPlans.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <CalendarOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                            <p style={{ marginTop: 16, color: '#999' }}>
                                No active work plans. Click "Create Work Plan" to get started.
                            </p>
                        </div>
                    ) : (
                        <Row gutter={[16, 16]}>
                            {workPlans.map(plan => (
                                <Col xs={24} sm={12} md={8} key={plan.id}>
                                    <Card
                                        size="small"
                                        actions={[
                                            <Tooltip title="Schedule Visit" key="schedule">
                                                <PlusOutlined onClick={() => handleScheduleVisit(plan)} />
                                            </Tooltip>,
                                            <Tooltip title="Edit" key="edit">
                                                <EditOutlined onClick={() => handleEditWorkPlan(plan)} />
                                            </Tooltip>,
                                            <Tooltip title="Delete" key="delete">
                                                <DeleteOutlined onClick={() => handleDeleteWorkPlan(plan.id)} />
                                            </Tooltip>
                                        ]}
                                    >
                                        <Card.Meta
                                            title={<span style={{ fontSize: '14px' }}>{plan.name}</span>}
                                            description={
                                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                                    <Tag color="blue">{plan.region}</Tag>
                                                    <div style={{ fontSize: '12px' }}>
                                                        {moment(plan.start_date).format('DD MMM')} - {moment(plan.end_date).format('DD MMM YYYY')}
                                                    </div>
                                                    <div style={{ fontSize: '12px' }}>
                                                        Facilities: {plan.total_facilities || 0} | Completed: {plan.completed_visits || 0}
                                                    </div>
                                                    {plan.description && (
                                                        <div style={{ fontSize: '11px', color: '#666' }}>
                                                            {plan.description.substring(0, 60)}...
                                                        </div>
                                                    )}
                                                </Space>
                                            }
                                        />
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}
                </Card>
            </Spin>

            {/* Work Plan Form Modal */}
            <WorkPlanForm
                visible={workPlanFormVisible}
                onCancel={() => { setWorkPlanFormVisible(false); setEditingWorkPlan(null); }}
                onSuccess={() => { loadAllData(); }}
                editData={editingWorkPlan}
            />

            {/* Visit Schedule Form Modal */}
            <VisitScheduleForm
                visible={visitScheduleFormVisible}
                onCancel={() => { setVisitScheduleFormVisible(false); setSelectedWorkPlanForVisit(null); }}
                onSuccess={() => { loadAllData(); }}
                workPlanId={selectedWorkPlanForVisit?.id}
                workPlanData={selectedWorkPlanForVisit}
                existingVisits={visits.filter(v => v.work_plan_id === selectedWorkPlanForVisit?.id)}
            />

            {/* Visit Details Modal */}
            <Modal
                title={<span><EyeOutlined /> Visit Details</span>}
                open={detailsVisible}
                onCancel={() => { setDetailsVisible(false); setSelectedVisit(null); }}
                footer={[<Button key="close" onClick={() => setDetailsVisible(false)}>Close</Button>]}
                width={700}
            >
                {selectedVisit && (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                            <strong>Facility:</strong> {selectedVisit.facility_name}
                        </div>
                        <div>
                            <strong>Region:</strong> <Tag>{selectedVisit.region}</Tag>
                        </div>
                        <div>
                            <strong>Facility Type:</strong> {selectedVisit.facility_level}
                        </div>
                        <div>
                            <strong>Scheduled Date:</strong> {moment(selectedVisit.scheduled_date).format('DD MMMM YYYY')}
                        </div>
                        {selectedVisit.actual_date && (
                            <div>
                                <strong>Actual Date:</strong> {moment(selectedVisit.actual_date).format('DD MMMM YYYY')}
                            </div>
                        )}
                        <div>
                            <strong>Status:</strong>
                            <Tag color={getStatusConfig(selectedVisit.display_status || selectedVisit.status).color}
                                style={{ marginLeft: 8 }}>
                                {getStatusConfig(selectedVisit.display_status || selectedVisit.status).text}
                            </Tag>
                        </div>

                        {/* ── NEW: Asset Types in detail modal ─────────────── */}
                        <div>
                            <strong>Asset Types:</strong>
                            <div style={{ marginTop: 6 }}>
                                <AssetTypeTags assetTypes={parseAssetTypes(selectedVisit.asset_types)} />
                            </div>
                        </div>
                        {/* ───────────────────────────────────────────────────── */}

                        {selectedVisit.assets_recorded_count > 0 && (
                            <div>
                                <strong>Assets Recorded:</strong>
                                <Tag color="cyan" icon={<CheckCircleOutlined />} style={{ marginLeft: 8 }}>
                                    {selectedVisit.assets_recorded_count} assets
                                </Tag>
                            </div>
                        )}
                        {selectedVisit.work_plan_name && (
                            <div>
                                <strong>Work Plan:</strong>
                                <Tag color="geekblue" style={{ marginLeft: 8 }}>{selectedVisit.work_plan_name}</Tag>
                            </div>
                        )}
                        {selectedVisit.notes && (
                            <div>
                                <strong>Notes:</strong>
                                <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                                    {selectedVisit.notes}
                                </div>
                            </div>
                        )}
                        {selectedVisit.findings && (
                            <div>
                                <strong>Findings:</strong>
                                <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                                    {selectedVisit.findings}
                                </div>
                            </div>
                        )}
                    </Space>
                )}
            </Modal>

            {/* Status Update Modal */}
            <StatusUpdateModal
                visible={statusUpdateModal}
                selectedVisit={selectedVisitForStatus}
                onClose={() => { setStatusUpdateModal(false); setSelectedVisitForStatus(null); }}
                onSuccess={() => { loadAllData(); }}
            />
        </div>
    );
};

export default WorkPlanIntegration;